import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

// Helper to format email type codes into user-friendly labels
const formatEmailType = (type) => {
  const labels = {
    'booking': 'Booking Confirmation',
    'cancellation': 'Cancellation',
    'waitlist': 'Waitlist Notification',
    'low_concession': 'Low Concession Alert',
    'concession': 'Concession Added',
    'setup': 'Account Invitation',
    'password_reset': 'Password Reset',
    'notification': 'Notification',
    'other': 'Other'
  };
  return labels[type] || type.replace(/_/g, ' ');
};

const DataCleanup = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [sizeInfo, setSizeInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [keepAliveStatus, setKeepAliveStatus] = useState(null);
  const [keepAliveLoading, setKeepAliveLoading] = useState(false);
  const [emailStats, setEmailStats] = useState(null);
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [showRestoreInfo, setShowRestoreInfo] = useState(false);
  
  // Cleanup state
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleanupDate, setCleanupDate] = useState('');
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  const MAX_SIZE_MB = 512; // MongoDB Atlas free tier limit

  // Fetch database statistics
  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/admin/database-stats');
      
      if (response.status === 200) {
        const data = response.data;
        setStats(data.stats);
        setSizeInfo(data.sizeInfo);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch scheduler status to check if cleanup is enabled
  const fetchSchedulerStatus = async () => {
    try {
      const response = await axios.get('/api/admin/scheduler-status');
      
      if (response.status === 200) {
        const data = response.data;
        setSchedulerStatus(data.status || data);
      }
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
    }
  };

  // Fetch keep-alive status
  const fetchKeepAliveStatus = async () => {
    try {
      const response = await axios.get('/api/admin/keepalive-status');
      
      if (response.status === 200) {
        const data = response.data;
        setKeepAliveStatus(data);
      }
    } catch (error) {
      console.error('Error fetching keep-alive status:', error);
    }
  };

  // Fetch email usage stats
  const fetchEmailStats = async () => {
    try {
      const response = await axios.get('/api/admin/email-stats');
      
      if (response.status === 200) {
        const data = response.data;
        setEmailStats(data);
      }
    } catch (error) {
      console.error('Error fetching email stats:', error);
    }
  };

  // Fetch backup status
  const fetchBackupStatus = async () => {
    try {
      const response = await axios.get('/api/admin/backup-status');
      
      if (response.status === 200) {
        const data = response.data;
        setBackupStatus(data);
      }
    } catch (error) {
      console.error('Error fetching backup status:', error);
    }
  };

  // Trigger manual backup
  const triggerBackup = async () => {
    setBackupLoading(true);
    try {
      const response = await axios.post('/api/admin/trigger-backup');
      
      if (response.status === 200) {
        const data = response.data;
        alert(`Backup completed! ${data.totalDocuments} documents backed up.`);
        fetchBackupStatus(); // Refresh backup status
      }
    } catch (error) {
      console.error('Error triggering backup:', error);
      alert('Error triggering backup');
    } finally {
      setBackupLoading(false);
    }
  };

  // Trigger manual keep-alive ping
  const triggerKeepAlive = async () => {
    setKeepAliveLoading(true);
    try {
      const response = await axios.post('/api/admin/trigger-keepalive');
      
      if (response.status === 200) {
        const data = response.data;
        setKeepAliveStatus({ lastPing: data.lastPing, isActive: true });
      }
    } catch (error) {
      console.error('Error triggering keep-alive:', error);
    } finally {
      setKeepAliveLoading(false);
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  // Perform cleanup by date
  const performCleanup = async () => {
    if (confirmPhrase !== 'DELETE OLD DATA') {
      alert('Please type "DELETE OLD DATA" to confirm');
      return;
    }
    
    setCleanupLoading(true);
    setCleanupResult(null);
    
    try {
      const response = await axios.post('/api/admin/cleanup-by-date', { cutoffDate: cleanupDate, confirmPhrase });
      
      const data = response.data;
      
      if (response.status === 200) {
        setCleanupResult({ success: true, message: data.message, details: data.result });
        // Refresh stats after cleanup
        fetchStats();
      } else {
        setCleanupResult({ success: false, message: data.message });
      }
    } catch (error) {
      setCleanupResult({ success: false, message: error.response?.data?.message || error.message });
    } finally {
      setCleanupLoading(false);
    }
  };

  // Get the max date for the date picker (30 days ago)
  const getMaxDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  };

  useEffect(() => {
    if (user?.canEdit()) {
      fetchStats();
      fetchSchedulerStatus();
      fetchKeepAliveStatus();
      fetchEmailStats();
      fetchBackupStatus();
    }
  }, [user]);

  if (!user?.canEdit()) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="badge-error px-4 py-3 rounded border">
          <strong>Access Denied:</strong> You don't have permission to access this page.
        </div>
      </div>
    );
  }

  const cleanupEnabled = schedulerStatus?.cleanupEnabled === true;
  
  // Estimate database size from document counts if actual size not available
  // Average document sizes (bytes): User ~800, Booking ~500, Class ~1200, Notification ~400
  const estimatedSizeMB = stats ? (
    ((stats.users || 0) * 800 + (stats.bookings || 0) * 500 + (stats.classes || 0) * 1200 + (stats.notifications || 0) * 400) / (1024 * 1024)
  ).toFixed(2) : null;
  
  // Use actual size if available, otherwise use estimate
  const displaySizeInfo = sizeInfo || (estimatedSizeMB ? {
    totalSizeMB: estimatedSizeMB,
    dataSizeMB: estimatedSizeMB,
    indexSizeMB: (parseFloat(estimatedSizeMB) * 0.15).toFixed(2), // Indexes typically ~15% of data
    storageSizeMB: estimatedSizeMB,
    isEstimate: true
  } : null);
  
  const usedPercentage = displaySizeInfo ? (parseFloat(displaySizeInfo.totalSizeMB) / MAX_SIZE_MB) * 100 : 0;
  const isNearLimit = usedPercentage > 80;
  const isCritical = usedPercentage > 95;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-primary mb-8">Database Statistics</h1>

        {/* Data Retention Notice */}
        <div className="badge-info px-4 py-3 rounded-lg mb-6 border">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <strong className="block mb-1">Data Preservation Policy</strong>
              <p className="text-sm">
                {cleanupEnabled ? (
                  <>Automatic data cleanup is <strong>enabled</strong>. Old data will be deleted based on retention settings.</>
                ) : (
                  <>All historical data is preserved indefinitely. Bookings, classes, and user activity history are never automatically deleted.</>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Database Size */}
        <div className="bg-card rounded-xl shadow-sm border border-primary p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-primary">Database Storage</h2>
              {displaySizeInfo?.isEstimate && (
                <span className="px-2 py-0.5 text-xs font-medium badge-warning rounded">
                  Estimated
                </span>
              )}
            </div>
            <span className="text-sm text-secondary">Free tier limit: {MAX_SIZE_MB} MB</span>
          </div>
          
          {displaySizeInfo ? (
            <div>
              {/* Estimate notice */}
              {displaySizeInfo.isEstimate && (
                <div className="mb-4 p-2 badge-warning rounded text-sm border">
                  Size is estimated based on document counts. Actual size may vary.
                </div>
              )}
              
              {/* Progress bar */}
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium text-secondary">
                    ~{displaySizeInfo.totalSizeMB} MB used
                  </span>
                  <span className={`font-medium ${isCritical ? 'text-red-500' : isNearLimit ? 'text-yellow-500' : 'text-green-500'}`}>
                    ~{(MAX_SIZE_MB - parseFloat(displaySizeInfo.totalSizeMB)).toFixed(2)} MB remaining
                  </span>
                </div>
                <div className="w-full bg-tertiary rounded-full h-4 overflow-hidden">
                  <div 
                    className={`h-4 rounded-full transition-all duration-500 ${
                      isCritical ? 'bg-red-500' : isNearLimit ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(usedPercentage, 100)}%` }}
                  />
                </div>
                <div className="text-right text-sm text-tertiary mt-1">
                  ~{usedPercentage.toFixed(1)}% used
                </div>
              </div>

              {/* Size breakdown - inline 3 columns */}
              <div className="flex gap-4 justify-center">
                <div className="flex-1 p-3 bg-tertiary rounded-lg border border-primary text-center">
                  <div className="text-lg font-bold text-blue-500">~{displaySizeInfo.dataSizeMB} MB</div>
                  <div className="text-xs text-tertiary">Data</div>
                </div>
                <div className="flex-1 p-3 bg-tertiary rounded-lg border border-primary text-center">
                  <div className="text-lg font-bold text-purple-500">~{displaySizeInfo.indexSizeMB} MB</div>
                  <div className="text-xs text-tertiary">Indexes</div>
                </div>
                <div className="flex-1 p-3 bg-tertiary rounded-lg border border-primary text-center">
                  <div className="text-lg font-bold text-green-500">~{displaySizeInfo.totalSizeMB} MB</div>
                  <div className="text-xs text-tertiary">Total</div>
                </div>
              </div>

              {/* Warning if near limit */}
              {isNearLimit && (
                <div className={`mt-4 p-3 rounded-lg border ${isCritical ? 'badge-error' : 'badge-warning'}`}>
                  <strong>{isCritical ? 'Critical:' : 'Warning:'}</strong> Database is ~{usedPercentage.toFixed(0)}% full. 
                  Consider cleaning up old data to free space.
                </div>
              )}
            </div>
          ) : (
            <div className="text-tertiary text-center py-4">
              {loading ? 'Loading size info...' : 'Size information not available'}
            </div>
          )}
        </div>

        {/* Email Usage */}
        <div className="bg-card rounded-xl shadow-sm border border-primary p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-primary">Mailgun Email Usage</h2>
            <span className="text-sm text-secondary">Free tier: {emailStats?.dailyLimit || 100} emails/day</span>
          </div>
          
          {emailStats ? (
            <div>
              {/* Daily progress bar */}
              <div className="mb-4">
                {(() => {
                  const usedToday = emailStats.today?.count || 0;
                  const dailyLimit = emailStats.dailyLimit || 100;
                  const percentage = (usedToday / dailyLimit) * 100;
                  const isNearEmailLimit = percentage > 80;
                  const isCriticalEmail = percentage > 95;
                  
                  return (
                    <>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-secondary">
                          {usedToday} emails sent today
                        </span>
                        <span className={`font-medium ${isCriticalEmail ? 'text-red-500' : isNearEmailLimit ? 'text-yellow-500' : 'text-green-500'}`}>
                          {dailyLimit - usedToday} remaining today
                        </span>
                      </div>
                      <div className="w-full bg-tertiary rounded-full h-4 overflow-hidden">
                        <div 
                          className={`h-4 rounded-full transition-all duration-500 ${
                            isCriticalEmail ? 'bg-red-500' : isNearEmailLimit ? 'bg-yellow-500' : 'bg-blue-500'
                          }`}
                          style={{ width: `${Math.min(percentage, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-sm text-tertiary mt-1">
                        <span>
                          {emailStats.today?.lastSent 
                            ? `Last sent: ${formatRelativeTime(emailStats.today.lastSent)}`
                            : 'No emails sent yet today'
                          }
                        </span>
                        <span>{percentage.toFixed(0)}% of daily limit</span>
                      </div>
                      
                      {/* Warning if near limit */}
                      {isNearEmailLimit && (
                        <div className={`mt-3 p-2 rounded-lg text-sm border ${isCriticalEmail ? 'badge-error' : 'badge-warning'}`}>
                          <strong>{isCriticalEmail ? 'Critical:' : 'Warning:'}</strong> Daily email quota is {percentage.toFixed(0)}% used. 
                          Limit resets at midnight UTC.
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Monthly total with type breakdown */}
              {emailStats.currentMonth && (
                <div className="p-3 bg-tertiary rounded-lg border border-primary text-sm mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-secondary">This month total:</span>
                    <span className="font-semibold text-primary">{emailStats.currentMonth.count || 0} emails</span>
                  </div>
                  {/* Type breakdown shown inline if available */}
                  {emailStats.currentMonth.byType && Object.keys(emailStats.currentMonth.byType).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-primary">
                      <div className="text-xs text-tertiary mb-1">Breakdown:</div>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(emailStats.currentMonth.byType).map(([type, count]) => (
                          <span key={type} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {formatEmailType(type)}: {count}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Recent days history */}
              {emailStats.recentDays && emailStats.recentDays.length > 0 && (
                <div className="mt-4 pt-4 border-t border-primary">
                  <div className="text-sm font-medium text-secondary mb-2">Last 7 Days</div>
                  <div className="flex gap-2 flex-wrap">
                    {emailStats.recentDays.map(d => (
                      <div 
                        key={d.date} 
                        className="text-center px-3 py-2 bg-tertiary rounded-lg border border-primary cursor-help"
                        title={d.byType && Object.keys(d.byType).length > 0 
                          ? Object.entries(d.byType).map(([type, count]) => `${formatEmailType(type)}: ${count}`).join('\n')
                          : 'No breakdown available'
                        }
                      >
                        <div className="text-xs text-tertiary">{d.date.slice(5)}</div>
                        <div className="font-semibold text-primary">{d.count}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-tertiary text-center py-4">
              {loading ? 'Loading email stats...' : 'Email usage tracking will start with the next email sent'}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="border-t-2 border-secondary my-8"></div>

        {/* Manual Cleanup Section */}
        <div className="bg-card rounded-xl shadow-sm p-6 mb-6 border-2 border-secondary">
          <h2 className="text-xl font-semibold text-primary mb-4">Manual Data Cleanup</h2>
          <p className="text-sm text-secondary mb-4">
            If you need to free up space, you can manually delete old data. This will remove 
            past classes and notifications older than the selected date.
          </p>
          <p className="text-sm text-green-600 dark:text-green-400 mb-4">
            <strong>User history is always preserved:</strong> All bookings, cancellations, and attendance records remain permanently.
          </p>
          
          <button
            onClick={() => setShowCleanupModal(true)}
            className="btn-secondary"
          >
            Open Cleanup Tool
          </button>
        </div>

        {/* Database Keep-Alive */}
        <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-xl font-semibold text-primary">Database Keep-Alive</h2>
              <p className="text-sm text-secondary mt-1">
                Prevents MongoDB Atlas free tier from auto-pausing after 30 days of inactivity
              </p>
            </div>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-tertiary rounded-lg border border-primary">
            <div>
              <div className="text-sm text-tertiary">Last Ping</div>
              <div className="font-semibold text-primary">
                {keepAliveStatus?.lastPing 
                  ? formatRelativeTime(keepAliveStatus.lastPing)
                  : 'Never pinged yet'
                }
              </div>
              {keepAliveStatus?.lastPing && (
                <div className="text-xs text-tertiary">
                  {new Date(keepAliveStatus.lastPing).toLocaleString()}
                </div>
              )}
            </div>
            <button
              onClick={triggerKeepAlive}
              disabled={keepAliveLoading}
              className="btn-primary text-sm"
            >
              {keepAliveLoading ? 'Pinging...' : 'Ping Now'}
            </button>
          </div>
          
          <div className="mt-4 p-3 badge-success rounded-lg text-sm border">
            <strong>Auto-ping active:</strong> The database is automatically pinged every 12 hours to keep it active.
          </div>
        </div>

        {/* Database Backups */}
        <div className="bg-card rounded-xl shadow-sm border border-primary p-6 mt-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-primary">Database Backups</h2>
              <button
                onClick={() => setShowRestoreInfo(true)}
                className="text-tertiary hover:text-primary transition-colors"
                title="How to restore from backup"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            </div>
            <button
              onClick={triggerBackup}
              disabled={backupLoading}
              className="btn-primary text-sm"
            >
              {backupLoading ? 'Backing up...' : 'Backup Now'}
            </button>
          </div>
          <p className="text-sm text-secondary mb-4">
            Weekly automated backups with 12-week retention
          </p>
          
          <div className="flex flex-wrap gap-4 justify-center mb-4">
            <div className="flex-1 min-w-[100px] p-4 bg-tertiary rounded-lg border border-primary text-center">
              <div className="text-sm text-tertiary mb-1">Backups</div>
              <div className="font-semibold text-blue-500">
                {backupStatus?.totalBackups || 0}
              </div>
            </div>
            <div className="flex-1 min-w-[100px] p-4 bg-tertiary rounded-lg border border-primary text-center">
              <div className="text-sm text-tertiary mb-1">Total Size</div>
              <div className="font-semibold text-orange-500">
                {backupStatus?.totalSizeFormatted || '0 B'}
              </div>
            </div>
            <div className="flex-1 min-w-[100px] p-4 bg-tertiary rounded-lg border border-primary text-center">
              <div className="text-sm text-tertiary mb-1">Last Backup</div>
              <div className="font-semibold text-green-500 text-sm">
                {backupStatus?.lastBackup?.createdAt 
                  ? formatRelativeTime(backupStatus.lastBackup.createdAt)
                  : 'Never'
                }
              </div>
            </div>
          </div>
          
          {backupStatus?.lastBackup && (
            <div className="p-3 bg-tertiary rounded-lg border border-primary text-sm">
              <div className="flex justify-between items-center flex-wrap gap-2">
                <span className="text-secondary">Latest: <strong className="text-primary">{backupStatus.lastBackup.name}</strong></span>
                <div className="flex gap-3 text-tertiary">
                  <span>{backupStatus.lastBackup.totalDocuments} docs</span>
                  <span>{backupStatus.lastBackup.sizeFormatted}</span>
                </div>
              </div>
            </div>
          )}
          
          <div className="mt-4 p-3 badge-info rounded-lg text-sm border">
            <strong>Auto-backup active:</strong> Database is backed up every Sunday at 3:00 AM UTC. Backups older than 12 weeks are automatically deleted.
          </div>
        </div>
      </div>

      {/* Cleanup Modal */}
      {showCleanupModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'var(--modal-overlay)' }}>
          <div className="bg-card rounded-lg shadow-xl max-w-md w-full p-6 border border-primary">
            <h3 className="text-xl font-bold text-primary mb-4">Manual Data Cleanup</h3>
            
            {/* Warning */}
            <div className="badge-warning px-4 py-3 rounded-lg mb-4 border">
              <strong className="block mb-1">Warning: This action is irreversible!</strong>
              <p className="text-sm">
                This will permanently delete past classes and notifications older than the selected date.
              </p>
            </div>
            
            {/* User history preserved notice */}
            <div className="badge-success px-4 py-3 rounded-lg mb-4 border">
              <strong className="block mb-1">User History Protected</strong>
              <p className="text-sm">
                All bookings, cancellations, and attendance records are preserved permanently.
              </p>
            </div>

            {/* Date Picker */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary mb-1">
                Delete data older than:
              </label>
              <input
                type="date"
                value={cleanupDate}
                onChange={(e) => setCleanupDate(e.target.value)}
                max={getMaxDate()}
                className="form-input w-full px-3 py-2 rounded-lg"
              />
              <p className="text-xs text-tertiary mt-1">
                Must be at least 30 days ago
              </p>
            </div>

            {/* Confirmation */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary mb-1">
                Type <strong>DELETE OLD DATA</strong> to confirm:
              </label>
              <input
                type="text"
                value={confirmPhrase}
                onChange={(e) => setConfirmPhrase(e.target.value)}
                placeholder="DELETE OLD DATA"
                className="form-input w-full px-3 py-2 rounded-lg"
              />
            </div>

            {/* Result message */}
            {cleanupResult && (
              <div className={`mb-4 p-3 rounded-lg border ${
                cleanupResult.success ? 'badge-success' : 'badge-error'
              }`}>
                {cleanupResult.message}
                {cleanupResult.details && (
                  <div className="text-sm mt-2">
                    <div>Classes deleted: {cleanupResult.details.deleted.classes}</div>
                    <div>Recurring instances deleted: {cleanupResult.details.deleted.recurringInstances}</div>
                    <div>Notifications deleted: {cleanupResult.details.deleted.notifications}</div>
                    <div className="text-green-600 dark:text-green-400 mt-1">✓ User history preserved</div>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCleanupModal(false);
                  setCleanupDate('');
                  setConfirmPhrase('');
                  setCleanupResult(null);
                }}
                className="flex-1 px-4 py-2 text-secondary bg-secondary rounded-lg hover:bg-hover transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={performCleanup}
                disabled={cleanupLoading || !cleanupDate || confirmPhrase !== 'DELETE OLD DATA'}
                className="flex-1 px-4 py-2 rounded-lg transition-colors font-medium"
                style={{
                  backgroundColor: (cleanupLoading || !cleanupDate || confirmPhrase !== 'DELETE OLD DATA') 
                    ? 'var(--text-muted)' 
                    : '#dc2626',
                  color: (cleanupLoading || !cleanupDate || confirmPhrase !== 'DELETE OLD DATA') 
                    ? 'var(--text-tertiary)' 
                    : '#ffffff',
                  cursor: (cleanupLoading || !cleanupDate || confirmPhrase !== 'DELETE OLD DATA') 
                    ? 'not-allowed' 
                    : 'pointer'
                }}
              >
                {cleanupLoading ? 'Deleting...' : 'Delete Old Data'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Info Modal */}
      {showRestoreInfo && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'var(--modal-overlay)' }}>
          <div className="bg-card rounded-lg shadow-xl max-w-lg w-full p-6 border border-primary max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-primary">How to Restore from Backup</h3>
              <button
                onClick={() => setShowRestoreInfo(false)}
                className="text-tertiary hover:text-primary transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4 text-sm">
              <div className="badge-info p-3 rounded-lg border">
                <strong>Backup Location:</strong> Backups are stored in the <code className="bg-tertiary px-1 rounded">backups/</code> folder on the server.
              </div>
              
              <div>
                <h4 className="font-semibold text-primary mb-2">Backup Contents</h4>
                <p className="text-secondary mb-2">Each backup folder contains JSON files for:</p>
                <ul className="list-disc list-inside text-secondary space-y-1 ml-2">
                  <li><code className="bg-tertiary px-1 rounded">users.json</code> - All user accounts (passwords excluded)</li>
                  <li><code className="bg-tertiary px-1 rounded">classes.json</code> - All class definitions</li>
                  <li><code className="bg-tertiary px-1 rounded">bookings.json</code> - All booking records</li>
                  <li><code className="bg-tertiary px-1 rounded">notifications.json</code> - All notifications</li>
                  <li><code className="bg-tertiary px-1 rounded">metadata.json</code> - Backup info and timestamps</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-primary mb-2">To Restore Data</h4>
                <ol className="list-decimal list-inside text-secondary space-y-2 ml-2">
                  <li>Access the server via SSH or file manager</li>
                  <li>Navigate to <code className="bg-tertiary px-1 rounded">backups/backup_YYYYMMDD_HHMM/</code></li>
                  <li>Use MongoDB tools to import the JSON files:</li>
                </ol>
                <div className="bg-tertiary p-3 rounded-lg mt-2 font-mono text-xs overflow-x-auto">
                  <div className="text-tertiary"># Import a collection (e.g., bookings)</div>
                  <div className="text-primary">mongoimport --uri "YOUR_MONGODB_URI" --collection bookings --file bookings.json --jsonArray --drop</div>
                </div>
              </div>
              
              <div className="badge-warning p-3 rounded-lg border">
                <strong>Warning:</strong> The <code className="bg-tertiary px-1 rounded">--drop</code> flag will replace existing data. Remove it to merge instead. Always backup current data before restoring.
              </div>
              
              <div>
                <h4 className="font-semibold text-primary mb-2">Alternative: Manual Review</h4>
                <p className="text-secondary">
                  Open the JSON files in any text editor to review historical data without importing. 
                  You can manually copy specific records back into the database if needed.
                </p>
              </div>
            </div>
            
            <div className="mt-6">
              <button
                onClick={() => setShowRestoreInfo(false)}
                className="w-full px-4 py-2 btn-primary rounded-lg"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataCleanup;
